// v10/src/components/admin/lifespan/AogOverrideModal.js
//
// A6.2 parte 2B — Modal de override AOG.
//
// Cuando el backend responde 409 con error_code: 'AIRCRAFT_AOG' al crear un
// flight_authorization, el frontend abre este modal:
//   • Muestra la lista de componentes vencidos (aogData.expired_components)
//   • Pide razón obligatoria (textarea, min 10 chars trim)
//   • Checkbox de doble confirmación
//   • Botón "Autorizar de todos modos" deshabilitado hasta que ambos
//     requisitos se cumplan
//
// También se usa para el 503 AOG_CHECK_UNAVAILABLE (fail-closed cuando el
// chequeo de AOG falla por timeout/error). En ese caso el caller pasa
// `unavailable={true}` y el copy del modal se adapta (no hay lista de
// componentes vencidos porque el backend no pudo determinarlos).
//
// Props:
//   isOpen      — bool (opcional, default true si está montado)
//   aogData     — payload del 409/503 + opcionalmente un `aircraft` inyectado
//                 por el caller con { registration } (el backend no lo trae).
//                 Si el payload trae `can_override: false`, el modal oculta el
//                 textarea/checkbox/botón de autorización y solo deja cerrar
//                 (variante bloqueo-puro usada por Parte 4 del registro rápido).
//   unavailable — bool (opcional). true → variante AOG_CHECK_UNAVAILABLE.
//   onConfirm   — async (overrideReason) => void  (el caller hace el retry)
//   onCancel    — () => void
//   darkMode    — bool

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertOctagon, X, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MIN_REASON_CHARS = 10;

const AogOverrideModal = ({
  isOpen = true,
  aogData,
  unavailable = false,
  onConfirm,
  onCancel,
  darkMode,
}) => {
  // El backend marca con can_override:false los flujos que NO permiten
  // autorización (ej. registro rápido en Parte 4 — el override es Parte 5).
  const blockOnly = aogData?.can_override === false;
  const { t } = useTranslation('admin');
  const [reason, setReason]         = useState('');
  const [confirmed, setConfirmed]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset state cuando se cierra/abre el modal con distinto payload
  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setConfirmed(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  // Cerrar con ESC mientras no estemos enviando
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onCancel]);

  const trimmedReason = reason.trim();
  const reasonValid   = trimmedReason.length >= MIN_REASON_CHARS;
  const canSubmit     = reasonValid && confirmed && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm?.(trimmedReason);
      // El caller decide qué hacer al éxito (cerrar el modal).
    } catch (_err) {
      // El error se muestra vía toast desde el caller. Reactivamos el botón.
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const fgSoft    = darkMode ? 'rgba(255,255,255,0.7)' : '#475569';

  const expired = Array.isArray(aogData?.expired_components) ? aogData.expired_components : [];
  const registration = aogData?.aircraft?.registration
    || aogData?.aircraft_registration
    || '';

  const remaining = Math.max(0, MIN_REASON_CHARS - trimmedReason.length);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 110,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
        }}
        onClick={submitting ? undefined : onCancel}
      >
        <motion.div
          initial={{ scale: 0.92, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 20 }}
          style={{
            width: '100%', maxWidth: 480, borderRadius: 24,
            background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
            padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <AlertOctagon style={{ width: 24, height: 24, color: '#dc2626' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {unavailable ? t('aog_check_unavailable_title') : t('aog_override_title')}
              </h3>
              {registration && (
                <p style={{ margin: '2px 0 0', fontSize: 13, color: fgMuted, fontFamily: 'monospace', fontWeight: 700 }}>
                  {registration}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              aria-label={t('aog_override_cancel')}
              style={{
                background: 'transparent', border: 'none',
                cursor: submitting ? 'not-allowed' : 'pointer',
                padding: 6, color: fgMuted, display: 'flex',
              }}
            >
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>

          {/* Lista de componentes vencidos */}
          {expired.length > 0 && (
            <div style={{
              padding: '12px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.06)',
              border: '1px solid rgba(239,68,68,0.18)',
              marginBottom: 16,
            }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('aog_override_blocked_by')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {expired.map((c, i) => {
                  const hOver = c.hours_overdue !== null && c.hours_overdue !== undefined ? Number(c.hours_overdue) : null;
                  const dOver = c.days_overdue  !== null && c.days_overdue  !== undefined ? Number(c.days_overdue)  : null;
                  const overdueLabel = hOver !== null && hOver > 0
                    ? t('aog_override_expired_hours', { hours: Math.round(hOver * 10) / 10 })
                    : dOver !== null && dOver > 0
                      ? t('aog_override_expired_days', { days: Math.round(dOver) })
                      : t('aog_override_expired_generic');
                  return (
                    <div key={c.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
                      <span style={{ flexShrink: 0 }}>❌</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: fgPrimary }}>{c.name || c.category || '—'}</div>
                        <div style={{ color: fgMuted, fontSize: 12 }}>{overdueLabel}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Explicación */}
          <p style={{ margin: '0 0 10px', fontSize: 14, color: fgSoft, lineHeight: 1.5 }}>
            {unavailable ? t('aog_check_unavailable_explanation') : t('aog_override_explanation')}
          </p>

          {!blockOnly && (
            <>
              {/* Textarea */}
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('aog_override_reason_placeholder')}
                rows={3}
                disabled={submitting}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '10px 12px', borderRadius: 12,
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                  background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                  color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
                  outline: 'none', resize: 'vertical', minHeight: 80,
                  marginBottom: 6,
                }}
              />
              <div style={{
                fontSize: 11,
                color: reasonValid ? '#16a34a' : (darkMode ? 'rgba(255,255,255,0.45)' : '#94a3b8'),
                marginBottom: 14, fontWeight: 600,
              }}>
                {reasonValid
                  ? t('aog_override_reason_ok')
                  : t('aog_override_reason_min_chars', { count: remaining })}
              </div>

              {/* Checkbox confirmación */}
              <label
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 12px', borderRadius: 12,
                  background: confirmed
                    ? 'rgba(239,68,68,0.06)'
                    : (darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc'),
                  border: confirmed
                    ? '1px solid rgba(239,68,68,0.25)'
                    : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0'),
                  marginBottom: 16, cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={submitting}
                  style={{ marginTop: 2, width: 16, height: 16, cursor: submitting ? 'not-allowed' : 'pointer', accentColor: '#dc2626' }}
                />
                <span style={{ fontSize: 13, color: fgSoft, lineHeight: 1.4 }}>
                  {t('aog_override_confirmation_text')}
                </span>
              </label>
            </>
          )}

          {/* Botones */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              style={{
                flex: 1, padding: 13, borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: blockOnly ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : 'transparent',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: blockOnly ? 700 : 600, fontSize: 14,
                color: blockOnly ? 'white' : fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {blockOnly ? 'Entendido' : t('aog_override_cancel')}
            </button>
            {!blockOnly && (
              <motion.button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                whileHover={canSubmit ? { scale: 1.02 } : {}}
                whileTap={canSubmit ? { scale: 0.97 } : {}}
                style={{
                  flex: 1.4, padding: 13, borderRadius: 14, border: 'none',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  background: canSubmit
                    ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                    : (darkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'),
                  color: canSubmit ? 'white' : (darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8'),
                  fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <style>{`@keyframes aog-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                {submitting && (
                  <Loader style={{ width: 16, height: 16, animation: 'aog-spin 1s linear infinite' }} />
                )}
                {submitting
                  ? t('aog_override_submitting')
                  : (unavailable ? t('aog_check_unavailable_confirm') : t('aog_override_confirm'))}
              </motion.button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AogOverrideModal;
